import React, { useEffect, useState } from "react";
import Head from "next/head";
import { useRouter } from "next/router";
import { ApolloProvider } from "@apollo/client/react";
import { useApollo } from "apiService/apolloClient";
import { StoreProvider } from "../store/store";
import { UserInfoProvider } from "../hocs/UserInfoContext";
import { Amplify } from "aws-amplify";
import { Hub } from "aws-amplify/utils";
import { signIn, getCurrentUser } from "aws-amplify/auth";
import LoadingShell from "components/LoadingShell/LoadingShell";

import { Analytics } from "@vercel/analytics/react";

import "../styles/globals.css";
import ModalProvider from "hocs/ModalContext";
import { MobileHeaderProvider } from "hocs/MobileHeaderContext";
import { ThemeProvider } from "hocs/ThemeContext";
import ConfirmationModal from "components/modals/ConfirmationModal";
import TermsAndConditions from "../components/modals/TermsAndConditions";
import AppShell from "components/AppShell/AppShell";
import { isSignedInEvent, isSignedOutEvent } from "helpers/authEvents";
import { hasClientSession } from "apiService/authService";

import { amplifyConfig } from "../utils/amplifyServerUtils";
import MySignIn from "../scene/SignIn/MySignIn";
import MyForgotPassword from "scene/SignIn/MyForgotPassword";
import MyRequireNewPassword from "scene/SignIn/MyRequireNewPassword";

Amplify.configure(amplifyConfig, { ssr: true });

// Module-level Hub listener - runs immediately when file loads.
//
// Event-name matching lives in `helpers/authEvents` — see the note there on the
// Amplify v5 -> v6 `signIn` -> `signedIn` rename that broke this listener.
//
// This reload is load-bearing, not cosmetic: it re-runs `getServerSideProps`
// on `/`, which is the ONLY place that redirects an authenticated user to
// `/valuation/list`. Without it, a fresh login leaves the user parked on `/`
// with the stale `isAuth: false` props from the pre-login SSR render, so
// `index.js` bails out of its redirect and renders LoadingShell forever.
//
// Safe by construction: v6 only dispatches `signedIn` after `getCurrentUser()`
// resolves, so by the time this fires the Cognito cookies are readable and the
// SSR gate on the next request will see them.
if (typeof window !== 'undefined') {
  Hub.listen('auth', (data) => {
    const { payload } = data;
    if (isSignedInEvent(payload.event)) {
      window.location.reload();
    }
  });
}

// ?token= auto-login: intercept before Authenticator renders.
// This replaces the old getDecryptedPass flow in MySignIn which used
// setNativeValue DOM hacks to programmatically fill React-controlled inputs.
// In v6 we call signIn() directly — no form puppeting needed.
// Raw base64 contains no "%", so a decode failure means the value was never
// encoded — fall back to it rather than throwing out of the effect.
function safeDecodeToken(raw) {
  try {
    return decodeURIComponent(raw);
  } catch (_) {
    return raw;
  }
}

function useTokenAutoLogin() {
  const [isTokenLogin, setIsTokenLogin] = useState(false);
  const [tokenLoginComplete, setTokenLoginComplete] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const url = window.location.href;
    const arr = url.split('token=');
    if (!arr || !arr[1]) return;

    setIsTokenLogin(true);
    // send-user-email.js percent-encodes the ciphertext into the href, but
    // /api/decrypt validates against /^[A-Za-z0-9+/=]+$/ — so an emailed link
    // arrives as %2B/%2F/%3D and is rejected with 400 every time (KMS blobs
    // effectively always contain +, / or =). Decode before sending it on.
    // Not URLSearchParams: it maps "+" to a space, which would break the
    // admin "copy link" path that builds ?token= with the raw, unencoded code.
    const token = safeDecodeToken(arr[1]);

    (async () => {
      try {
        const res = await fetch('/api/decrypt', {
          method: 'POST',
          body: JSON.stringify({ code: token }),
        });
        const body = await res.json();
        const { code } = body;
        const user = JSON.parse(code);
        if (!user) {
          setIsTokenLogin(false);
          return;
        }
        // Fix HTML entity encoding from password reset emails
        user.password = user.password.replace('&amp;', '&');

        // Strip the token from the URL BEFORE signing in. A successful signIn
        // dispatches the `signedIn` Hub event, whose listener reloads the page —
        // that reload can win the race against any cleanup queued after signIn,
        // leaving `?token=` in the URL and re-running this whole flow on every
        // reload. Cleaning first makes the reload idempotent.
        window.history.replaceState({}, '', window.location.pathname);

        await signIn({ username: user.username, password: user.password });
        // signIn success dispatches the Hub `signedIn` event, which reloads the page
        setTokenLoginComplete(true);
      } catch (e) {
        console.error('[_app.js] Token auto-login failed:', e);
        setIsTokenLogin(false);
      }
    })();
  }, []);

  return { isTokenLogin, tokenLoginComplete };
}

// Pages served without a session: no auth gate, no AppShell, no WHOAMI. Keep this list short —
// everything else in the app assumes an authenticated bank/company/organisation user.
const PUBLIC_ROUTES = ["/developers"];

// Client-side replacement for the cookie-presence check that used to live in
// every page's getServerSideProps. Same predicate, same destination — an
// unauthenticated deep link still ends up on `/` — but it costs nothing,
// where the server version made Next block every navigation on a serverless
// round-trip. `/` itself keeps its getServerSideProps: it is the only place
// that redirects an *authenticated* user on to /valuation/list, and the Hub
// listener above depends on that.
function useCookieAuthGate(router) {
  useEffect(() => {
    if (PUBLIC_ROUTES.includes(router.pathname)) return;
    if (router.pathname === "/") return;
    if (hasClientSession()) return;
    router.replace("/");
  }, [router, router.pathname]);
}

function MyApp({ Component, pageProps }) {
  const router = useRouter();
  useCookieAuthGate(router);
  const apolloClient = useApollo(pageProps);
  const { isTokenLogin, tokenLoginComplete } = useTokenAutoLogin();

  // Auth route management — replaces Authenticator's internal state machine.
  // Our custom components call signIn()/resetPassword() directly, so we only
  // need simple route state for navigating between auth screens.
  const [authRoute, setAuthRoute] = useState('signIn');

  // Session gate: check for existing session before deciding whether
  // to show auth UI. This matches what withAuthenticator did in v4.
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  useEffect(() => {
    const devBypass =
      process.env.NODE_ENV === 'development' &&
      process.env.NEXT_PUBLIC_DEV_BYPASS_AUTH === 'true';
    const devUser = process.env.NEXT_PUBLIC_DEV_USERNAME;
    const devPass = process.env.NEXT_PUBLIC_DEV_PASSWORD;

    // Amplify v6 with ssr:true uses CookieStorage. After the Hub 'signIn'
    // event triggers a page reload, cookies may not be readable in the first
    // tick. Retry getCurrentUser() with short delays before falling back.
    const tryGetCurrentUser = async (retries = 3, delayMs = 150) => {
      for (let attempt = 0; attempt <= retries; attempt++) {
        try {
          await getCurrentUser();
          return true;
        } catch (_) {
          if (attempt < retries) {
            await new Promise(r => setTimeout(r, delayMs));
          }
        }
      }
      return false;
    };

    tryGetCurrentUser()
      .then(async (authenticated) => {
        if (authenticated) {
          setIsAuthenticated(true);
          setCheckingAuth(false);
          return;
        }
        // No existing session — auto-login with dev credentials if available
        if (devBypass && devUser && devPass) {
          try {
            await signIn({ username: devUser, password: devPass });
            setIsAuthenticated(true);
          } catch (err) {
            console.error('[_app.js] Dev auto-login failed:', err.message);
            setIsAuthenticated(false);
          }
        } else {
          setIsAuthenticated(false);
        }
        setCheckingAuth(false);
      });
  }, []);

  // Listen for sign-in/sign-out events to update the gate
  useEffect(() => {
    const unsubscribe = Hub.listen('auth', (data) => {
      if (isSignedInEvent(data.payload.event)) {
        setIsAuthenticated(true);
      }
      if (isSignedOutEvent(data.payload.event)) {
        setIsAuthenticated(false);
      }
    });
    return unsubscribe;
  }, []);

  const renderApp = () => {
    // Public pages render immediately, whether or not a session exists.
    if (PUBLIC_ROUTES.includes(router.pathname)) {
      return <>
        <Head>
          <meta name="viewport" content="width=device-width, initial-scale=1" />
        </Head>
        <Component {...pageProps} />
        <Analytics />
      </>;
    }

    // Show branded loading shell during token auto-login or auth checking
    if ((isTokenLogin && !tokenLoginComplete) || checkingAuth) {
      return <>
        <Head>
          <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover" />
        </Head>
        <LoadingShell />
        <Analytics />
      </>;
    }

    // No existing session — show custom auth UI
    if (!isAuthenticated) {
      const authScreen = (() => {
        switch (authRoute) {
          case 'forgotPassword':
            return <MyForgotPassword onNavigate={setAuthRoute} />;
          case 'forceNewPassword':
            return <MyRequireNewPassword onNavigate={setAuthRoute} />;
          default:
            return <MySignIn onNavigate={setAuthRoute} />;
        }
      })();
      return <>
        <Head>
          <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover" />
        </Head>
        {authScreen}
        <Analytics />
      </>;
    }

    // Authenticated — render the app. UserInfoProvider handles its own
    // loading state with LoadingShell while WHOAMI completes.
    return (
      <div>
        <Head>
          <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover" />
        </Head>
        {pageProps?.isAuth === false && <LoadingShell />}
        <StoreProvider>
          <ApolloProvider client={apolloClient}>
            <UserInfoProvider>
              <MobileHeaderProvider>
                <ModalProvider>
                  <AppShell>
                    <Component {...pageProps} />
                  </AppShell>
                  <ConfirmationModal />
                  <TermsAndConditions />
                </ModalProvider>
              </MobileHeaderProvider>
            </UserInfoProvider>
          </ApolloProvider>
        </StoreProvider>
        <Analytics />
      </div>
    );
  };

  return <ThemeProvider>{renderApp()}</ThemeProvider>;
}

export default MyApp;
